import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { getLocal, NFeDto, STORAGENFE_KEY, updateLocal } from "../../NFe.Dto";
import { meioPagamento } from "../../../NFCe/NFCe.Dto";
import { FloatInput, FloatNumericInput, showToast } from "src/componentes";
import {
  FormType,
  getTipoIntegracao,
  schema,
} from "./NFe.Cadastro.Pagamento.Dto";
import { useEffect, useMemo } from "react";

const NFeCadastroPagamentoView = () => {
  const nf = getLocal<NFeDto>(STORAGENFE_KEY);
  const pagamentos = nf?.pagamento;
  const itens = nf?.itens;
  const finalidade = nf?.finalidadeEmissao;

  const totalProdutos = itens
    ? Number(
        itens
          .reduce(
            (acc, item) =>
              acc +
              Number(
                (
                  item.valorUnitarioComercial * item.quantidadeComercial
                ).toFixed(2)
              ),
            0
          )
          .toFixed(2)
      )
    : 0;

  const navigate = useNavigate();

  const handleVoltar = () => {
    navigate("../item");
  };

  const pagamentosTratados = useMemo(
    () =>
      pagamentos?.map((p) => ({
        ...p,
        valorPagamento: Number(Number(p.valorPagamento ?? 0).toFixed(2)),
      })),
    [pagamentos]
  );

  useEffect(() => {
    if (pagamentos && pagamentos.length > 0) {
      const needsFix = pagamentos.some(
        (p) =>
          p.valorPagamento !== Number(Number(p.valorPagamento ?? 0).toFixed(2))
      );
      if (needsFix) {
        updateLocal<NFeDto>(STORAGENFE_KEY, {
          pagamento: pagamentosTratados,
        });
      }
    }
  }, [pagamentos, pagamentosTratados]);

  const form = useForm<FormType>({
    resolver: yupResolver(schema),
    defaultValues: {
      dadosParcelas: pagamentosTratados ?? [
        { meioPagamento: 1, valorPagamento: totalProdutos },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "dadosParcelas",
  });

  const rawDadosParcelas = useWatch({
    control: form.control,
    name: "dadosParcelas",
  });

  const dadosParcelasWatch = useMemo(
    () => rawDadosParcelas ?? [],
    [rawDadosParcelas]
  );

  const totalPagamentos = useMemo(
    () =>
      Number(
        dadosParcelasWatch
          .reduce(
            (acc, dp) =>
              acc + Number(Number(dp.valorPagamento ?? 0).toFixed(2)),
            0
          )
          .toFixed(2)
      ),
    [dadosParcelasWatch]
  );

  const onSubmit = (data: FormType) => {
    const parcelas = (data.dadosParcelas ?? []).map((dp) => ({
      ...dp,
      valorPagamento: Number(Number(dp.valorPagamento ?? 0).toFixed(2)),
      tipoIntegracao: getTipoIntegracao(dp.meioPagamento),
    }));

    const totalParcelas = parcelas.reduce(
      (acc, dp) => acc + Math.round((dp.valorPagamento ?? 0) * 100),
      0
    );

    const totalProdutosInt = Math.round(totalProdutos * 100);

    //4 - Devolução de mercadoria
    if (totalParcelas !== totalProdutosInt && finalidade !== 4) {
      showToast({
        message: "O total dos pagamentos deve ser igual ao total dos produtos.",
        variant: "Danger",
        title: "Erro",
      });
      return;
    }

    updateLocal<NFeDto>(STORAGENFE_KEY, { pagamento: parcelas });

    navigate("../modalidadefrete");
  };

  const formatBR = (valor: number) =>
    valor.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const DefinirTipoIntegracao = (
    meioPagamentoSelecionado: number,
    index: number
  ) => {
    form.setValue(
      `dadosParcelas.${index}.tipoIntegracao`,
      getTipoIntegracao(meioPagamentoSelecionado)
    );
  };

  return (
    <>
      <Form noValidate onSubmit={form.handleSubmit(onSubmit)}>
        <div className="mt-3" style={{ marginBottom: "100px" }}>
          {fields.map((field, index) => {
            const meioPagamentoWatch =
              dadosParcelasWatch?.[index]?.meioPagamento;
            return (
              <Row key={field.id} className="mb-3">
                <Col xs={12} className="mb-2 d-flex justify-content-between">
                  <strong>Pagamento {index + 1}</strong>

                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => remove(index)}
                  >
                    Remover
                  </Button>
                </Col>
                <Col xs={6}>
                  <Form.Select
                    {...form.register(`dadosParcelas.${index}.meioPagamento`)}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      form.setValue(
                        `dadosParcelas.${index}.meioPagamento`,
                        value,
                        {
                          shouldDirty: true,
                          shouldTouch: true,
                          shouldValidate: true,
                        }
                      );
                      DefinirTipoIntegracao(value, index);
                    }}
                  >
                    <option value="">Selecione</option>
                    {meioPagamento.map((mp) => (
                      <option key={mp.meioPagamento} value={mp.meioPagamento}>
                        {mp.descricaoPagamento}
                      </option>
                    ))}
                  </Form.Select>

                  {form.formState.errors.dadosParcelas?.[index]
                    ?.meioPagamento && (
                    <small className="text-danger">
                      {
                        form.formState.errors.dadosParcelas[index]
                          ?.meioPagamento?.message
                      }
                    </small>
                  )}
                </Col>

                <Col xs={6} className="mt-1">
                  <FloatNumericInput
                    label=""
                    name={`dadosParcelas.${index}.valorPagamento`}
                    control={form.control}
                    useFloatingLabel={false}
                    errorsMessage={
                      form.formState.errors.dadosParcelas?.[index]
                        ?.valorPagamento
                    }
                  />
                </Col>
                {Number(meioPagamentoWatch) === 99 && (
                  <Col xs={12}>
                    <FloatInput
                      label="Descrição"
                      inputMask="maiusculas"
                      registerField={form.register(
                        `dadosParcelas.${index}.descricaoMeioPagamento`
                      )}
                      errorsMessage={
                        form.formState.errors.dadosParcelas?.[index]
                          ?.descricaoMeioPagamento
                      }
                    />
                  </Col>
                )}
              </Row>
            );
          })}

          <div className="d-grid mt-3">
            <Button
              variant="outline-primary"
              onClick={() =>
                append({
                  meioPagamento: 1,
                  valorPagamento: 0,
                })
              }
            >
              + Adicionar Pagamento
            </Button>
          </div>
        </div>

        <div className="fixed-bottom">
          <Container className="py-3">
            <Row className="mb-3">
              <Col xs={4}>Produtos: {formatBR(totalProdutos)}</Col>
              <Col xs={4} className="ps-0 pe-0 text-center">
                Pagamentos: {formatBR(totalPagamentos)}
              </Col>
              <Col xs={4} className="text-end">
                Diferença:{" "}
                {formatBR(
                  Math.abs(totalProdutos - totalPagamentos) < 0.001
                    ? 0
                    : totalProdutos - totalPagamentos
                )}
              </Col>
            </Row>
            <Row>
              <Col xs={6} className="d-grid">
                <Button variant="outline-secondary" onClick={handleVoltar}>
                  Voltar
                </Button>
              </Col>
              <Col xs={6} className="d-grid">
                <Button variant="outline-primary" type="submit">
                  Próximo
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      </Form>
    </>
  );
};

export default NFeCadastroPagamentoView;
